/* rv-testimonials.js — carousel behaviour for the rv-testimonials section (original code).
   The track is a native CSS scroll-snap scroller (swipe works without JS). This script adds:
   - infinite loop: one set of clones before and after the real cards; when a scroll settles on a clone
     the track jumps (instantly) to the matching real card,
   - prev/next arrows and dots, keyboard arrows while the section has focus,
   - the measured resting frame: on the >= 750px layout the leading full card is real card 1 and the
     active dot follows the card peeking on the LEFT (dot 0 while cards 1-3 are the full cards); on
     mobile the leading (centred) card is real card 0 and dot 0 is that card,
   - optional autoplay (off by default — the original has none; interval + eased slide duration from the
     section settings), paused on hover, focus, touch/pointer down, when the tab is hidden and while the
     section is off screen,
   - scroll-gated fade-up of the wrapper: the wrapper is visible by default; setup() ARMS the paused
     pre-reveal state (.rv-testimonials--armed) synchronously and the observer adds
     .rv-testimonials--in-view, so the section can never stay hidden if this file does not run,
   - theme editor hooks (re-init on section load, scroll to a selected block).
   prefers-reduced-motion: no autoplay, no fade, instant moves. */
(function () {
  'use strict';

  var SEL_ROOT = '[data-rv-tm]';
  var CLASS_ARMED = 'rv-testimonials--armed';
  var CLASS_IN_VIEW = 'rv-testimonials--in-view';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;

  function isDesktop() {
    return desktopQuery ? desktopQuery.matches : window.innerWidth >= 750;
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function setup(root) {
    if (root.hasAttribute('data-rv-tm-ready')) return;
    root.setAttribute('data-rv-tm-ready', '');

    var ready = false;
    var inView = true;
    if (!reduceMotion && 'IntersectionObserver' in window) {
      root.classList.add(CLASS_ARMED);
      inView = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
          if (inView) root.classList.add(CLASS_IN_VIEW);
          if (ready) schedule();
        });
      }, { threshold: 0 });
      io.observe(root);
    }

    var track = root.querySelector('[data-rv-tm-track]');
    var list = root.querySelector('[data-rv-tm-list]');
    if (!track || !list) return;
    var real = Array.prototype.slice.call(list.querySelectorAll('[data-rv-tm-card]'));
    var n = real.length;
    if (n < 2) return;

    var durationAttr = parseInt(root.getAttribute('data-duration'), 10);
    var opts = {
      autoplay: root.getAttribute('data-autoplay') === 'true',
      interval: Math.max(1, parseFloat(root.getAttribute('data-interval')) || 5) * 1000,
      duration: isNaN(durationAttr) ? 300 : Math.max(0, durationAttr),
      loop: root.getAttribute('data-loop') === 'true',
      pauseHover: root.getAttribute('data-pause-hover') === 'true'
    };

    var clones = 0;
    if (opts.loop) {
      real.forEach(function (card) {
        var before = makeClone(card);
        list.insertBefore(before, real[0]);
        list.appendChild(makeClone(card));
      });
      clones = n;
    }
    var all = Array.prototype.slice.call(list.children);
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-rv-tm-dot]'));
    var prev = root.querySelector('[data-rv-tm-prev]');
    var next = root.querySelector('[data-rv-tm-next]');

    /* Dot d = leading card (real index d + dotOffset): the desktop dot names the left-peeking card. */
    function dotOffset() {
      return isDesktop() ? 1 : 0;
    }

    var index = clones + dotOffset();
    var animating = false;
    var animFrame = null;
    var timer = null;
    var settleTimer = null;
    var paused = false;
    var pressed = false;

    function makeClone(card) {
      var c = card.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-rv-tm-clone', '');
      c.removeAttribute('data-rv-tm-card');
      c.removeAttribute('data-shopify-editor-block');
      c.removeAttribute('role');
      c.removeAttribute('aria-label');
      c.removeAttribute('aria-roledescription');
      Array.prototype.forEach.call(c.querySelectorAll('a, button, [tabindex]'), function (el) {
        el.setAttribute('tabindex', '-1');
      });
      return c;
    }

    function posOf(i) {
      return all[i].offsetLeft - all[0].offsetLeft;
    }

    function pitch() {
      return all.length > 1 ? all[1].offsetLeft - all[0].offsetLeft : 1;
    }

    function realIndex(i) {
      return ((i - clones) % n + n) % n;
    }

    function dotIndex(i) {
      return ((realIndex(i) - dotOffset()) % n + n) % n;
    }

    /* Highest index whose snap position the track can actually reach (all of them in loop mode). */
    function lastReachable() {
      if (opts.loop) return all.length - 1;
      var reach = Math.floor((track.scrollWidth - track.clientWidth + 1) / pitch());
      return Math.max(0, Math.min(all.length - 1, reach));
    }

    function clamp(i) {
      return Math.max(0, Math.min(lastReachable(), i));
    }

    /* Track index to show for dot d: same clone set as the current index in loop mode (normalize() then
       brings it back), the real card itself otherwise. */
    function targetForDot(d) {
      var r = (d + dotOffset()) % n;
      return opts.loop ? index + (r - realIndex(index)) : r;
    }

    function stopAnim() {
      if (animFrame) window.cancelAnimationFrame(animFrame);
      animFrame = null;
      animating = false;
      track.classList.remove('is-animating');
    }

    function jump(i) {
      stopAnim();
      index = clamp(i);
      track.classList.add('is-animating');
      track.scrollLeft = posOf(index);
      void track.offsetWidth;
      track.classList.remove('is-animating');
      updateDots();
    }

    function normalize() {
      if (!opts.loop) return;
      if (index < clones || index >= clones + n) jump(clones + realIndex(index));
    }

    function goTo(i, animate) {
      i = clamp(i);
      if (!animate || reduceMotion || opts.duration === 0) {
        jump(i);
        normalize();
        schedule();
        return;
      }
      stopAnim();
      index = i;
      updateDots();
      var from = track.scrollLeft;
      var to = posOf(i);
      var start = null;
      animating = true;
      track.classList.add('is-animating');
      function step(ts) {
        if (start === null) start = ts;
        var t = Math.min(1, (ts - start) / opts.duration);
        track.scrollLeft = from + (to - from) * easeInOutCubic(t);
        if (t < 1) {
          animFrame = window.requestAnimationFrame(step);
        } else {
          track.scrollLeft = to;
          stopAnim();
          normalize();
          schedule();
        }
      }
      animFrame = window.requestAnimationFrame(step);
    }

    function move(delta) {
      var target = index + delta;
      if (!opts.loop) {
        var last = lastReachable();
        if (target > last) target = 0;
        if (target < 0) target = last;
      }
      goTo(target, true);
    }

    function updateDots() {
      var active = dotIndex(index);
      var last = lastReachable();
      dots.forEach(function (dot, i) {
        var on = i === active;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
        dot.disabled = !opts.loop && targetForDot(i) > last;
      });
    }

    /* A user scroll (swipe / wheel) has come to rest: adopt the nearest snap index. */
    function settle() {
      if (animating) return;
      index = clamp(Math.round(track.scrollLeft / pitch()));
      updateDots();
      normalize();
      schedule();
    }

    function onScroll() {
      if (animating) return;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 120);
    }

    function schedule() {
      if (timer) clearTimeout(timer);
      timer = null;
      if (!opts.autoplay || reduceMotion || paused || pressed || !inView || document.hidden) return;
      timer = setTimeout(function () { move(1); }, opts.interval);
    }

    function pause() { paused = true; schedule(); }
    function resume() { paused = false; schedule(); }
    function press() { pressed = true; stopAnim(); schedule(); }
    function release() {
      if (!pressed) return;
      pressed = false;
      schedule();
    }

    track.addEventListener('scroll', onScroll, { passive: true });
    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(targetForDot(i), true); });
    });

    if (opts.pauseHover) {
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
    }
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) resume();
    });
    /* Press starts on the track; the release may land anywhere (drag out, over the arrows, outside the
       window), so it is listened for on window and the pressed flag is also dropped when the pointer leaves. */
    track.addEventListener('pointerdown', press);
    track.addEventListener('touchstart', press, { passive: true });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      window.addEventListener(ev, release, { passive: true });
    });
    ['touchend', 'touchcancel'].forEach(function (ev) {
      track.addEventListener(ev, release, { passive: true });
    });
    root.addEventListener('mouseleave', release);
    document.addEventListener('visibilitychange', schedule);
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { jump(index); }, 150);
    });

    /* Theme editor: jump to the block being edited (as the leading card) and hold autoplay. */
    root.addEventListener('shopify:block:select', function (e) {
      var card = e.target && e.target.closest ? e.target.closest('[data-rv-tm-card]') : null;
      if (!card) return;
      paused = true;
      goTo(clones + parseInt(card.getAttribute('data-index'), 10), false);
    });
    root.addEventListener('shopify:block:deselect', function () { paused = false; schedule(); });

    ready = true;
    jump(index);
    schedule();
  }

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(SEL_ROOT), setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
